import { useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApiCall from '../../../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../../../hooks/useGetGeneralAppData';
import ClientUsersService from '../../../../../../services/ClientUsersService';
import { SelectedPermissionType, UserTypeType } from '../../types';

interface IUseFormSubmit {
  type: UserTypeType,
  name: string,
  cpf: string,
  email: string,
  selectedPermissions: SelectedPermissionType[],
  resetFields: () => void,
}

export default function useFormSubmit({
  type,
  name,
  cpf,
  email,
  selectedPermissions,
  resetFields,
}: IUseFormSubmit) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { apiCall } = useApiCall();
  const navigate = useNavigate();
  const { id } = useParams();
  const { userCode, userProfile, companyCode, userName } = useGetGeneralAppData();

  const createUser = useCallback(async () => {
    await apiCall({
      apiToCall: ClientUsersService.createClientUser,
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        dadosUsuario: {
          tipoContatoCod: type.value,
          codEmpresa: companyCode,
          nome: name,
          cpf,
          email,
          alteraSen: false,
          perfilUsuCad: 10,
        },
        permissoesNav: selectedPermissions.map((permission) => ({
          codPag: permission.pageCode,
          pagina: permission.pageLabel,
          incluir: permission.add,
          vizualizar: permission.view,
          alterar: permission.update,
          imprimir: permission.download,
          excluir: permission.delete,
        }))
      }),
      onStartLoad: () => setIsSubmitting(true),
      onEndLoad: () => setIsSubmitting(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível adicionar o usuário ${name}. Por favor, tente novamente. (${response.msg})`);
          return;
        }
        resetFields();
        toast.success(`O usuário ${name} foi adicionado com sucesso!`);
      },
      catchMessage: `Não foi possível adicionar o usuário ${name}. Por favor, tente novamente.`
    });
  }, [apiCall, companyCode, cpf, email, name, resetFields, selectedPermissions, type?.value, userCode, userName, userProfile]);

  const editUser = useCallback(async () => {
    await apiCall({
      apiToCall: ClientUsersService.updateClientUser,
      reqBody: JSON.stringify({
        codEmpresa: companyCode,
        codUsu: userCode,
        perfilUsu: userProfile,
        usuario: userName,
        dadosUsuario: {
          tipoContatoCod: type.value,
          codEmpresa: companyCode,
          nome: name,
          cpf,
          email,
          alteraSen: false,
          codUsuAcesso: id,
          perfilUsuCad: 10,
        },
        permissoesNav: selectedPermissions.map((permission) => ({
          codPag: permission.pageCode,
          pagina: permission.pageLabel,
          incluir: permission.add,
          vizualizar: permission.view,
          alterar: permission.update,
          imprimir: permission.download,
          excluir: permission.delete,
        }))
      }),
      onStartLoad: () => setIsSubmitting(true),
      onEndLoad: () => setIsSubmitting(false),
      actionAfterResponse: (response) => {
        if (!response.codigo) {
          toast.error(`Não foi possível editar o usuário ${name}. Por favor, tente novamente. (${response.msg})`);
          return;
        }
        toast.success(`O usuário ${name} foi adicionado com sucesso!`);
        navigate('/clientUsers');
      },
      catchMessage: `Não foi possível editar o usuário ${name}. Por favor, tente novamente.`
    });
  }, [apiCall, companyCode, cpf, email, id, name, navigate, selectedPermissions, type?.value, userCode, userName, userProfile]);

  return {
    isSubmitting,
    createUser,
    editUser,
  };
}
